"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { type EmailPreviewSrcDocOptions, buildEmailPreviewSrcDoc } from "@/lib/email-preview";
import { getHtmlPreviewSourceScrollTop } from "@/lib/html-preview-scroll-sync";
import { HTML_PREVIEW_SOURCE_ID_ATTR } from "@/lib/html-preview-source-map";
import { cx } from "@/lib/tailwind-utils";
interface EmailPreviewIframeProps extends EmailPreviewSrcDocOptions {
  className?: string;
  title?: string;
  highlightOnHover?: boolean;
  activeSourceId?: string | null;
  scrollToSourcePosition?: EmailPreviewScrollPosition | null;
  onSourceHover?: (sourceId: string | null) => void;
  onScrollSourcePositionChange?: (position: EmailPreviewScrollPositionPayload) => void;
}
interface EmailPreviewScrollPosition {
  sourceId: string;
  viewportRatio: number;
  updateKey: number;
}
type EmailPreviewScrollPositionPayload = Omit<EmailPreviewScrollPosition, "updateKey">;
const HOVER_TARGET_SELECTOR = ["a[href]", "button", "img", "table", "thead", "tbody", "tfoot", "tr", "td", "th", "section", "article", "header", "main", "footer", "div", "p", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "blockquote"].join(",");
const PLATFORM_HIGHLIGHT_FALLBACKS = {
  primary: "74 93 78",
  primaryForeground: "250 246 240",
  ring: "74 93 78"
};
const SCROLL_SYNC_VIEWPORT_RATIO = 0.3;
function getPlatformThemeRgb(variableName: string, fallback: string): string {
  return window.getComputedStyle(window.document.documentElement).getPropertyValue(variableName).trim() || fallback;
}
function clampRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) return 0;
  return Math.min(1, Math.max(0, ratio));
}
function getScrollElement(doc: Document): HTMLElement | null {
  return doc.scrollingElement as HTMLElement | null ?? doc.documentElement ?? doc.body;
}
function resolveHoverTarget(target: EventTarget | null): HTMLElement | null {
  if (!target || typeof (target as Element).closest !== "function" || typeof (target as Element).getBoundingClientRect !== "function") {
    return null;
  }
  const element = target as Element;
  return element.closest(`[${HTML_PREVIEW_SOURCE_ID_ATTR}]`) as HTMLElement | null ?? element.closest(HOVER_TARGET_SELECTOR) as HTMLElement | null;
}
interface HoverHighlightController {
  cleanup: () => void;
  setActiveSourceId: (sourceId: string | null) => void;
}
interface ScrollSyncController {
  cleanup: () => void;
  scrollToSourcePosition: (position: EmailPreviewScrollPositionPayload) => void;
}
function getSourceElementById(doc: Document, sourceId: string): HTMLElement | null {
  return doc.querySelector<HTMLElement>(`[${HTML_PREVIEW_SOURCE_ID_ATTR}="${sourceId}"]`);
}
function getNearestSourceElementToViewportAnchor(doc: Document, anchorY: number): HTMLElement | null {
  const sourceElements = Array.from(doc.querySelectorAll<HTMLElement>(`[${HTML_PREVIEW_SOURCE_ID_ATTR}]`));
  let nearestElement: HTMLElement | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const element of sourceElements) {
    if (element === doc.body || element === doc.documentElement) continue;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 && rect.height <= 0) continue;
    const distance = anchorY < rect.top ? rect.top - anchorY : Math.max(0, anchorY - rect.bottom);
    if (distance < nearestDistance) {
      nearestElement = element;
      nearestDistance = distance;
    }
  }
  return nearestElement;
}
function getSourceElementFromViewportAnchor(doc: Document, win: Window, viewportRatio: number): HTMLElement | null {
  const anchorX = win.innerWidth / 2;
  const anchorY = win.innerHeight * clampRatio(viewportRatio);
  const elements = typeof doc.elementsFromPoint === "function" ? doc.elementsFromPoint(anchorX, anchorY) : [];
  for (const element of elements) {
    const sourceElement = element.closest?.(`[${HTML_PREVIEW_SOURCE_ID_ATTR}]`) as HTMLElement | null;
    if (sourceElement && sourceElement !== doc.body && sourceElement !== doc.documentElement) {
      return sourceElement;
    }
  }
  return getNearestSourceElementToViewportAnchor(doc, anchorY);
}
function getViewportSourceScrollPosition(doc: Document, win: Window, viewportRatio: number): EmailPreviewScrollPositionPayload | null {
  const element = getSourceElementFromViewportAnchor(doc, win, viewportRatio);
  if (!element) return null;
  const sourceId = element.getAttribute(HTML_PREVIEW_SOURCE_ID_ATTR);
  if (!sourceId) return null;
  return {
    sourceId,
    viewportRatio
  };
}
function installHoverHighlight(iframe: HTMLIFrameElement | null, onSourceHover?: (sourceId: string | null) => void): HoverHighlightController | null {
  const doc = iframe?.contentDocument;
  const win = doc?.defaultView;
  if (!doc?.body || !win) return null;
  const primary = getPlatformThemeRgb("--primary", PLATFORM_HIGHLIGHT_FALLBACKS.primary);
  const primaryForeground = getPlatformThemeRgb("--primary-foreground", PLATFORM_HIGHLIGHT_FALLBACKS.primaryForeground);
  const ring = getPlatformThemeRgb("--ring", PLATFORM_HIGHLIGHT_FALLBACKS.ring);
  const overlay = doc.createElement("div");
  overlay.setAttribute("aria-hidden", "true");
  overlay.style.cssText = ["position:fixed", "z-index:2147483647", "pointer-events:none", "display:none", "box-sizing:border-box", `border:2px solid rgb(${ring} / 0.95)`, "border-radius:3px", `background:rgb(${primary} / 0.12)`, `box-shadow:0 0 0 1px rgb(${primaryForeground} / 0.9),0 8px 24px rgb(${primary} / 0.2)`].join(";");
  doc.body.appendChild(overlay);
  let hoveredElement: HTMLElement | null = null;
  let lastSourceId: string | null = null;
  const notifySourceHover = (sourceId: string | null) => {
    if (sourceId === lastSourceId) return;
    lastSourceId = sourceId;
    onSourceHover?.(sourceId);
  };
  const hideOverlay = (notify = true) => {
    hoveredElement = null;
    overlay.style.display = "none";
    if (notify) notifySourceHover(null);
  };
  const updateOverlay = () => {
    if (!hoveredElement || !doc.body.contains(hoveredElement)) {
      hideOverlay();
      return;
    }
    const rect = hoveredElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      hideOverlay();
      return;
    }
    overlay.style.display = "block";
    overlay.style.left = `${rect.left}px`;
    overlay.style.top = `${rect.top}px`;
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;
  };
  const highlightElement = (element: HTMLElement | null, notify = true) => {
    if (!element || element === doc.body || element === doc.documentElement) {
      hideOverlay(notify);
      return;
    }
    hoveredElement = element;
    if (notify) {
      notifySourceHover(element.getAttribute(HTML_PREVIEW_SOURCE_ID_ATTR));
    }
    updateOverlay();
  };
  const handleMouseMove = (event: MouseEvent) => {
    highlightElement(resolveHoverTarget(event.target));
  };
  const handleMouseLeave = () => hideOverlay();
  doc.addEventListener("mousemove", handleMouseMove);
  doc.addEventListener("mouseleave", handleMouseLeave);
  win.addEventListener("scroll", updateOverlay, true);
  win.addEventListener("resize", updateOverlay);
  return {
    cleanup: () => {
      doc.removeEventListener("mousemove", handleMouseMove);
      doc.removeEventListener("mouseleave", handleMouseLeave);
      win.removeEventListener("scroll", updateOverlay, true);
      win.removeEventListener("resize", updateOverlay);
      overlay.remove();
    },
    setActiveSourceId: (sourceId: string | null) => {
      if (!sourceId) {
        hideOverlay(false);
        return;
      }
      const element = doc.querySelector<HTMLElement>(`[${HTML_PREVIEW_SOURCE_ID_ATTR}="${sourceId}"]`);
      highlightElement(element, false);
    }
  };
}
function installScrollSync(iframe: HTMLIFrameElement | null, onScrollSourcePositionChange: (position: EmailPreviewScrollPositionPayload) => void): ScrollSyncController | null {
  const doc = iframe?.contentDocument;
  const win = doc?.defaultView;
  if (!doc?.body || !win) return null;
  let scrollFrame: number | null = null;
  let suppressScrollNotify = false;
  const releaseSuppression = () => {
    win.requestAnimationFrame(() => {
      win.requestAnimationFrame(() => {
        suppressScrollNotify = false;
      });
    });
  };
  const notifyScrollSourcePosition = () => {
    if (suppressScrollNotify || scrollFrame !== null) return;
    scrollFrame = win.requestAnimationFrame(() => {
      scrollFrame = null;
      if (suppressScrollNotify) return;
      const position = getViewportSourceScrollPosition(doc, win, SCROLL_SYNC_VIEWPORT_RATIO);
      if (position) onScrollSourcePositionChange(position);
    });
  };
  win.addEventListener("scroll", notifyScrollSourcePosition, true);
  return {
    cleanup: () => {
      win.removeEventListener("scroll", notifyScrollSourcePosition, true);
      if (scrollFrame !== null) {
        win.cancelAnimationFrame(scrollFrame);
      }
    },
    scrollToSourcePosition: (position: EmailPreviewScrollPositionPayload) => {
      const scrollElement = getScrollElement(doc);
      if (!scrollElement) return;
      const element = getSourceElementById(doc, position.sourceId);
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const viewportHeight = win.innerHeight || scrollElement.clientHeight;
      const maxScrollTop = Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight);
      const nextScrollTop = getHtmlPreviewSourceScrollTop({
        maxScrollTop,
        sourceTop: scrollElement.scrollTop + rect.top,
        viewportHeight,
        viewportRatio: position.viewportRatio
      });
      suppressScrollNotify = true;
      scrollElement.scrollTo({
        top: nextScrollTop,
        left: scrollElement.scrollLeft,
        behavior: "auto"
      });
      releaseSuppression();
    }
  };
}

/**
 * Renders email HTML in a sandboxed iframe. Used for compose preview and
 * review preview in blasts, and shared with email-body-renderer patterns.
 */
export function EmailPreviewIframe(t0) {
  const $ = _c(42);
  const {
    html,
    showFooter,
    useComposeStyles,
    emptyPlaceholder,
    className,
    title: t1,
    highlightOnHover,
    activeSourceId,
    scrollToSourcePosition,
    onSourceHover,
    onScrollSourcePositionChange
  } = t0;
  const title = t1 === undefined ? "Email preview" : t1;
  const iframeRef = useRef(null);
  const highlightControllerRef = useRef(null);
  const scrollSyncControllerRef = useRef(null);
  const scrollToSourcePositionRef = useRef(null);
  const activeSourceIdRef = useRef(activeSourceId ?? null);
  let t2;
  if ($[0] !== emptyPlaceholder || $[1] !== html || $[2] !== showFooter || $[3] !== useComposeStyles) {
    t2 = buildEmailPreviewSrcDoc({
      html,
      showFooter,
      useComposeStyles,
      emptyPlaceholder
    });
    $[0] = emptyPlaceholder;
    $[1] = html;
    $[2] = showFooter;
    $[3] = useComposeStyles;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const srcDoc = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      highlightControllerRef.current?.cleanup();
      highlightControllerRef.current = null;
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const cleanupHighlight = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      scrollSyncControllerRef.current?.cleanup();
      scrollSyncControllerRef.current = null;
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const cleanupScrollSync = t4;
  let t5;
  if ($[7] !== highlightOnHover || $[8] !== onSourceHover) {
    t5 = () => {
      cleanupHighlight();
      if (!highlightOnHover) {
        return;
      }
      highlightControllerRef.current = installHoverHighlight(iframeRef.current, onSourceHover);
      highlightControllerRef.current?.setActiveSourceId(activeSourceIdRef.current);
    };
    $[7] = highlightOnHover;
    $[8] = onSourceHover;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const attachHoverHighlight = t5;
  let t6;
  if ($[10] !== onScrollSourcePositionChange) {
    t6 = () => {
      cleanupScrollSync();
      if (!onScrollSourcePositionChange) {
        return;
      }
      scrollSyncControllerRef.current = installScrollSync(iframeRef.current, onScrollSourcePositionChange);
      const latestScrollPosition = scrollToSourcePositionRef.current;
      if (latestScrollPosition) {
        scrollSyncControllerRef.current?.scrollToSourcePosition(latestScrollPosition);
      }
    };
    $[10] = onScrollSourcePositionChange;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const attachScrollSync = t6;
  let t7;
  if ($[12] !== attachHoverHighlight || $[13] !== attachScrollSync) {
    t7 = () => {
      attachHoverHighlight();
      attachScrollSync();
    };
    $[12] = attachHoverHighlight;
    $[13] = attachScrollSync;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const attachIframeBehaviors = t7;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => {
      cleanupHighlight();
      cleanupScrollSync();
    };
    t9 = [cleanupHighlight, cleanupScrollSync];
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[17] !== attachHoverHighlight || $[18] !== highlightOnHover) {
    t10 = () => {
      if (highlightOnHover) {
        if (!highlightControllerRef.current) {
          attachHoverHighlight();
        }
      } else {
        cleanupHighlight();
      }
    };
    t11 = [attachHoverHighlight, cleanupHighlight, highlightOnHover];
    $[17] = attachHoverHighlight;
    $[18] = highlightOnHover;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[21] !== attachScrollSync) {
    t12 = () => {
      attachScrollSync();
    };
    t13 = [attachScrollSync];
    $[21] = attachScrollSync;
    $[22] = t12;
    $[23] = t13;
  } else {
    t12 = $[22];
    t13 = $[23];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[24] !== onScrollSourcePositionChange) {
    t14 = () => {
      if (!onScrollSourcePositionChange) {
        cleanupScrollSync();
      }
    };
    t15 = [cleanupScrollSync, onScrollSourcePositionChange];
    $[24] = onScrollSourcePositionChange;
    $[25] = t14;
    $[26] = t15;
  } else {
    t14 = $[25];
    t15 = $[26];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[27] !== activeSourceId) {
    t16 = () => {
      activeSourceIdRef.current = activeSourceId ?? null;
      highlightControllerRef.current?.setActiveSourceId(activeSourceIdRef.current);
    };
    t17 = [activeSourceId];
    $[27] = activeSourceId;
    $[28] = t16;
    $[29] = t17;
  } else {
    t16 = $[28];
    t17 = $[29];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[30] !== scrollToSourcePosition) {
    t18 = () => {
      scrollToSourcePositionRef.current = scrollToSourcePosition ?? null;
      if (!scrollToSourcePosition) {
        return;
      }
      scrollSyncControllerRef.current?.scrollToSourcePosition(scrollToSourcePosition);
    };
    t19 = [scrollToSourcePosition];
    $[30] = scrollToSourcePosition;
    $[31] = t18;
    $[32] = t19;
  } else {
    t18 = $[31];
    t19 = $[32];
  }
  useEffect(t18, t19);
  let t20;
  if ($[33] !== className) {
    t20 = cx("w-full bg-background", className);
    $[33] = className;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  const t21 = highlightOnHover ? "true" : undefined;
  const t22 = onScrollSourcePositionChange ? "true" : undefined;
  let t23;
  if ($[35] !== attachIframeBehaviors || $[36] !== srcDoc || $[37] !== t20 || $[38] !== t21 || $[39] !== t22 || $[40] !== title) {
    t23 = <iframe ref={iframeRef} srcDoc={srcDoc} title={title} className={t20} data-highlight-on-hover={t21} data-scroll-sync={t22} onLoad={attachIframeBehaviors} sandbox="allow-same-origin" />;
    $[35] = attachIframeBehaviors;
    $[36] = srcDoc;
    $[37] = t20;
    $[38] = t21;
    $[39] = t22;
    $[40] = title;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  return t23;
}